/**
 * Runtime configuration interface loaded from K8s ConfigMap
 * This is injected via config.js in index.html before the app bundle loads
 */
export interface RuntimeConfig {
  VITE_API_BASE_URL?: string
  /**
   * Where the platform's API lives, as a prefix for every call.
   *
   * Since the BFF (phase 9.6) this is the *same origin* as the UI: the ingress routes `/api`, `/bff`,
   * `/hubs` and `/signin-oidc` on this host through to the orchestrator, which is what makes the session
   * cookie host-scoped and keeps CORS credentials out of the arrangement entirely (checklist D5). An empty
   * value means exactly that, and is the correct value for a deployment — it is not a missing setting.
   */
  VITE_ORCHESTRATOR_URL?: string
  /** Feature flag to enable SignalR instead of SSE (default: false) */
  VITE_USE_SIGNALR?: string
}

/**
 * The API prefix to build request URLs from: the configured origin, or the empty string for same-origin.
 *
 * Deliberately not `getConfig`, which throws on an empty value. Same-origin is the deployed arrangement
 * and the empty string is how it is spelled — treating it as a configuration error would refuse to start
 * the one configuration the BFF requires.
 */
export function apiOrigin(): string {
  return (getConfigOptional('VITE_ORCHESTRATOR_URL') ?? '').replace(/\/$/, '')
}

/**
 * Extend Window interface with runtime config
 */
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig
  }
}

/**
 * Get config value - prefers runtime config, falls back to build-time env
 * @param key - The config key (e.g., 'VITE_API_BASE_URL')
 * @throws Error if value is not defined
 */
export function getConfig(key: keyof RuntimeConfig): string {
  const runtimeConfig = window.__RUNTIME_CONFIG__
  if (runtimeConfig && key in runtimeConfig) {
    const value = runtimeConfig[key]
    if (!value) {
      throw new Error(`Configuration error: ${key} is not defined in runtime config`)
    }
    return value
  }
  const value = import.meta.env[key]
  if (!value) {
    throw new Error(`Configuration error: ${key} is not defined in environment variables`)
  }
  return value
}

/**
 * Get config value optionally - returns undefined if not set
 * @param key - The config key (e.g., 'VITE_API_BASE_URL')
 * @returns The value or undefined if not defined
 */
export function getConfigOptional(key: keyof RuntimeConfig): string | undefined {
  const runtimeConfig = window.__RUNTIME_CONFIG__
  if (runtimeConfig && key in runtimeConfig) {
    const value = runtimeConfig[key]
    if (value) {
      return value
    }
  }
  const value = import.meta.env[key]
  if (value) {
    return value
  }
  return undefined
}
